import { useCallback, useState } from 'react'
import Cropper, { type Area } from 'react-easy-crop'
import { getCroppedFile } from './cropImage'

interface CropModalProps {
  /** Object URL of the user-selected image. */
  imageSrc: string
  /** Target aspect ratio (width / height). */
  aspect: number
  /** Maximum longest-edge dimension of the cropped output. */
  maxDim: number
  /** Original file name, used to name the cropped file. */
  fileName: string
  onCancel: () => void
  onApply: (file: File) => void
}

export function CropModal({ imageSrc, aspect, maxDim, fileName, onCancel, onApply }: CropModalProps) {
  const [crop, setCrop] = useState({ x: 0, y: 0 })
  const [zoom, setZoom] = useState(1)
  const [pixels, setPixels] = useState<Area | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const onCropComplete = useCallback((_area: Area, areaPixels: Area) => {
    setPixels(areaPixels)
  }, [])

  async function handleApply() {
    if (!pixels) return
    setBusy(true)
    setError(null)
    try {
      const file = await getCroppedFile(imageSrc, pixels, maxDim, fileName)
      onApply(file)
    } catch {
      setError('Could not crop image. Please try a different file.')
      setBusy(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-black/40 z-[600] flex items-center justify-center p-4">
      <div
        className="bg-surface rounded-2xl p-5 max-w-[460px] w-full"
        style={{ boxShadow: '0 8px 40px rgba(0,0,0,0.2)' }}
      >
        <h2 className="m-0 mb-1 text-[18px]">Adjust photo</h2>
        <p className="m-0 mb-4 text-[13px] text-ink-2">Drag to reposition and zoom so the important part stays in frame.</p>

        <div className="relative w-full rounded-xl overflow-hidden bg-black" style={{ height: 280 }}>
          <Cropper
            image={imageSrc}
            crop={crop}
            zoom={zoom}
            aspect={aspect}
            onCropChange={setCrop}
            onZoomChange={setZoom}
            onCropComplete={onCropComplete}
          />
        </div>

        <div className="flex items-center gap-3 mt-4">
          <span className="text-[12px] text-muted">Zoom</span>
          <input
            type="range"
            min={1}
            max={3}
            step={0.01}
            value={zoom}
            onChange={(e) => setZoom(Number(e.target.value))}
            className="flex-1 cursor-pointer"
            aria-label="Zoom"
          />
        </div>

        {error && <p className="mt-2 text-[12px] text-warn">{error}</p>}

        <div className="flex gap-2.5 justify-end mt-4">
          <button
            type="button"
            onClick={onCancel}
            disabled={busy}
            className="px-4 py-2 rounded-lg border border-line-soft bg-surface text-ink text-[13px] cursor-pointer font-sans disabled:opacity-60"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleApply}
            disabled={busy || !pixels}
            className="px-4 py-2 rounded-lg border-0 text-white text-[13px] font-semibold cursor-pointer font-sans disabled:opacity-60 bg-primary"
          >
            {busy ? 'Saving…' : 'Use photo'}
          </button>
        </div>
      </div>
    </div>
  )
}
